// Import React and hooks from React
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";

// Import components
import CreateButton from "global/components/CreateButton";

// Import utilities
import { deepEqual } from "global/utilities/CheckData";


// SettingsEditor component that manages editable key/value settings
export function SettingsEditor({ settingsArray, onSettingsChange, disabled = false, watchKey }) {
  
  //console.log("settingsArray: ", settingsArray);
  //console.log("WatchKey: ", watchKey);
  
  // Create a ref to hold an incrementing counter for stable local setting row ids
  const idRef = useRef(0);

  // Create a ref to remember the last watchKey we synced for (selected MMM item identity)
  const watchKeyRef = useRef(watchKey);

  // Define a helper that returns the next unique local id each time it is called
  const nextId = () => (++idRef.current);



  // Define a converter that turns API shape [{Key:Value}] into editable rows [{id,key,value}]
  const toRows = useCallback((arr) => (

    // Guard against non-arrays and map each singleton object into an editable row
    Array.isArray(arr)

      // Map each object like { Colour: "Blue" } into { id, key: "Colour", value: "Blue" }
      ? arr.map((obj) => {

          // Extract the single key from the object (or empty string if none)
          const key = Object.keys(obj || {})[0] ?? "";

          // Extract the value by that key (or empty string if missing)
          const value = obj?.[key] ?? "";

          // Return a normalized row with a fresh local id
          return { id: nextId(), key, value };

        })

        // If not an array, fall back to an empty list
      : []

    // Empty dependency array means this function identity stays stable
  ), []);


  
  // Define a converter that turns editable rows [{id,key,value}] back into [{Key:Value}]
  const toSingletonObjects = useCallback((rows) => (

    // For each row, create a singleton object with the key as property and value as its value
    (rows || []).map(({ key, value }) => ({ [key]: value }))

  // toSingletonObjects depends on nothing, keep identity stable
  ), []);



  // Initialize local editable draft rows from the incoming settingsArray prop (from itemDetails)
  const [draftRows, setDraftRows] = useState(() => toRows(settingsArray));



  /// CRITICAL: only re-sync local draft when the selected record (watchKey) actually changes
  useEffect(() => {

    // Check if the incoming watchKey differs from the one we last synced
    if (watchKeyRef.current !== watchKey) {

      // Update the ref to the new watchKey so we know we have synced for it
      watchKeyRef.current = watchKey;

      // Rebuild editable rows from the latest settingsArray prop
      const rowsFromProps = toRows(settingsArray);

      // Replace the local draft with the newly built rows
      setDraftRows(rowsFromProps);

      // Rebase the id counter to the highest existing row id so future inserts keep increasing
      idRef.current = rowsFromProps.reduce((m, r) => Math.max(m, r.id || 0), 0);

      //console.log("[SettingsEditor] re-sync from props due to watchKey change:", watchKey);

    }

  // Depend only on watchKey so this effect runs when selected MMM item  changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchKey]);

  

  // Convert current rows to correct format for API call and update formValues via handleChange
  const updateFormValues = useCallback((rows) => {

    // Convert rows to the array-of-singletons API shape
    const nextSettings = toSingletonObjects(rows);

    // Call the parent handler to update form state (handleChange)
    onSettingsChange(nextSettings);

  }, [onSettingsChange, toSingletonObjects]);


  
  // Handle a change to either "key" or "value" for a given row
  const handleRowChange = (rowId, field, newValue) => {

    // Compute the next local draft rows by mapping and updating the matching row by id
    const next = draftRows.map((r) => (r.id === rowId ? { ...r, [field]: newValue } : r));

    // Update local draft immediately so typing feels instant
    setDraftRows(next);

    // Push the converted data to parent component right away so formValues stays in sync
    updateFormValues(next);

  };



  // Remove a row by id
  const handleRemove = (rowId) => {

    // Filter out the row to delete
    const next = draftRows.filter((r) => r.id !== rowId);

    // Update local draft
    setDraftRows(next);

    // Update the formValues
    updateFormValues(next);

  };



  // Add a new blank row
  const handleAddSetting = () => {

    // Create the new row with a stable id
    const newRow = { id: nextId(), key: "", value: "" };

    // Append to local draft
    const next = [...draftRows, newRow];

    // Update local draft
    setDraftRows(next);

    // Update the formValues
    updateFormValues(next);

  };



  // Work out duplicate-key flags (this doesn't block anything but we can create a warning)
  const duplicateMap = useMemo(() => {

    // Count each key’s frequency
    const counts = draftRows.reduce((acc, { key }) => {

      // Increment count for this key
      acc[key] = (acc[key] || 0) + 1;

      // Return accumulator
      return acc;

    }, {});

    // Return the map of key -> count
    return counts;

    // Rerun when draftRows changes
  }, [draftRows]);



  // React effect to keep local draftRows aligned with incoming props when the CONTENT changes
  useEffect(() => {

    // Convert the latest incoming settingsArray (array of singletons) into editable row objects
    const rowsFromProps = toRows(settingsArray);

    // Convert our current draft rows back into the array-of-singletons shape for fair comparison
    const singletonFromDraft = toSingletonObjects(draftRows);

    // Determine if the content from props differs from what we're currently showing
    const contentChanged = !deepEqual(singletonFromDraft, settingsArray);

    // If the content truly changed (not just a new reference), re-seed the editor rows
    if (contentChanged) {

      // Replace local draft rows with rows built from the latest props
      setDraftRows(rowsFromProps);

      // Rebase the local id counter to the highest row id so new rows get unique, increasing ids
      idRef.current = rowsFromProps.reduce((m, r) => Math.max(m, r.id || 0), 0);

    }

  // Re-run this effect only when the props array changes, or the converter functions change identity
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsArray, toRows, toSingletonObjects]);



  // Render the editor UI
  return (

    // Wrapper for the settings editor
    <div className="settings-editor">
     
      <br/>
      
      <div className="settings-editor__rows">
        

        {draftRows.length === 0 &&
        
          <p>No settings have been added.</p>

        }

        {draftRows.map((row) => {

          // Determine if this row key duplicates any other
          const isDuplicate = row.key !== "" && (duplicateMap[row.key] || 0) > 1;

          // Render a single editable row with Key and Value inputs and Delete button
          return (

            // Single row wrapper with stable key
            <div className="" key={row.id}>
                            
              <div className="settings-editor-row">

                <input
                  className={`settings-editor__input ${isDuplicate ? "settings-editor__input--error" : ""}`}
                  type="text"
                  name={`settings.${row.id}.key`}
                  value={row.key}
                  onChange={(e) => handleRowChange(row.id, "key", e.target.value)}
                  placeholder="Enter key (e.g., Colour)"
                  disabled={disabled}
                />
                
                <input
                  className="settings-editor__input"
                  type="text"
                  name={`settings.${row.id}.value`}
                  value={row.value}
                  onChange={(e) => handleRowChange(row.id, "value", e.target.value)}
                  placeholder="Enter value (e.g., Blue)"
                  disabled={disabled}
                />

                <CreateButton tooltip="Remove Setting" icon="/images/icon_bin.png" size="xs" onClick={() => handleRemove(row.id)} disabled={disabled} type="button" />

              </div>  

              {isDuplicate && (

                <div className="settings-editor__hint settings-editor__hint--error">
                  Duplicate key — consider renaming
                </div>
                
              )}

            </div>

          );

        })}

      </div>
      
      <div className="settings-editor__footer">

        <CreateButton tooltip="Add Setting" title="+ Add Setting" size="s" onClick={handleAddSetting} disabled={disabled} type="button" />

      </div>
          
    </div>

  );

}

